import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material'

import useAppStore from '$appStore'
import { writeMultisiteSelectedCompanies } from '$utils'
import { ILoadedVenue } from '$models'

import VenueTriggerButton from './VenueTriggerButton'
import VenuePanel from './VenuePanel'
import { VENUE_SEARCH_THRESHOLD, filterVenuesByQuery } from './venueFilter'
import { REACT_MULTISITE_SELECTION_CHANGE_EVENT } from './venueSwitcherBridge'

type MultisiteVenueSelectorProps = {
  readonly venues: readonly ILoadedVenue[]
  readonly selectedIds: readonly string[]
  readonly anchorElement: HTMLElement | null
  readonly onOpen: (event: React.MouseEvent<HTMLElement>) => void
  readonly onClose: () => void
}

/**
 * Multisite mode of the venue switcher is a checkbox multi-select with Select all
 * / Unselect all controls, used while a multisite report is being shown.
 * The venue switcher in this scenario shows how many venues are selected (e.g.
 * "4 of 12 selected").
 *
 * When the selection of venues changes here we publish a `@@loaded/multisiteSelectionChange`
 * event which the AngularJS app is listening for - when it hears that it'll
 * trigger a reload of the report to show the updated set of venues.
 */
const MultisiteVenueSelector = ({
  venues,
  selectedIds,
  anchorElement,
  onOpen,
  onClose,
}: MultisiteVenueSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('')

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  // ILoadedVenue.id is typed optional; a venue without an id can't be selected
  // or toggled, so we work with the id-bearing venues throughout.
  const venuesWithId = useMemo(
    () =>
      venues.filter((venue): venue is ILoadedVenue & { id: string } =>
        Boolean(venue.id)
      ),
    [venues]
  )

  const searchEnabled = venuesWithId.length > VENUE_SEARCH_THRESHOLD

  const filteredVenues = useMemo(
    () => filterVenuesByQuery(venuesWithId, searchQuery),
    [venuesWithId, searchQuery]
  )

  // Derived selection summary. Memoised together so a keystroke in the search
  // field (which re-renders this component via local state) doesn't re-scan the
  // full venue list on every render.
  const { selectedCount, allSelected, noneSelected, label } = useMemo(() => {
    const count = venuesWithId.filter((venue) =>
      selectedIdSet.has(venue.id)
    ).length
    const all = count === venuesWithId.length

    // The trigger label: "All venues" when everything is selected, the single
    // venue's name when only one is, otherwise a count.
    const computedLabel = (() => {
      if (all) {
        return 'All venues'
      }
      if (count === 1) {
        const onlyVenue = venuesWithId.find((venue) =>
          selectedIdSet.has(venue.id)
        )
        return onlyVenue?.shortName ?? '1 venue'
      }
      return `${count} venues`
    })()

    return {
      selectedCount: count,
      allSelected: all,
      noneSelected: count === 0,
      label: computedLabel,
    }
  }, [venuesWithId, selectedIdSet])

  // Reset the filter on close so reopening always shows the full list.
  const handleClose = () => {
    setSearchQuery('')
    onClose()
  }

  const persistAndNotify = (nextSelectedIds: string[]) => {
    // 1. Persist so a refresh resolves the same selection.
    writeMultisiteSelectedCompanies(nextSelectedIds)

    // 2. Update our own store so the panel reflects the change immediately -
    //    React owns this state
    useAppStore.setState({ multisiteSelectedCompanies: nextSelectedIds })

    // 3. Signal AngularJS that the selection changed (no payload - AngularJS reads
    //    the new value back from storage) so the open report rebuilds.
    window.dispatchEvent(
      new CustomEvent(REACT_MULTISITE_SELECTION_CHANGE_EVENT)
    )
  }

  const handleToggleVenue = (venueId: string) => {
    const next = selectedIdSet.has(venueId)
      ? selectedIds.filter((id) => id !== venueId)
      : [...selectedIds, venueId]

    persistAndNotify(next)
  }

  const handleSelectAll = () => {
    persistAndNotify(venuesWithId.map((venue) => venue.id))
  }

  const handleUnselectAll = () => {
    persistAndNotify([])
  }

  return (
    <Box>
      <VenueTriggerButton
        label={label}
        isOpen={Boolean(anchorElement)}
        onClick={onOpen}
      />

      <VenuePanel
        anchorElement={anchorElement}
        onClose={handleClose}
        title="Venues in report"
        subtitle={`${selectedCount} of ${venuesWithId.length} selected`}
        searchEnabled={searchEnabled}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        emptyMessage={
          filteredVenues.length === 0
            ? `No venues match '${searchQuery.trim()}'`
            : null
        }
        headerActions={
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            paddingLeft={1}
            paddingRight={2}
            paddingBottom={1}
          >
            <Button
              size="small"
              onClick={handleSelectAll}
              disabled={allSelected}
              sx={{ textTransform: 'none' }}
            >
              Select all
            </Button>
            <Button
              size="small"
              onClick={handleUnselectAll}
              disabled={noneSelected}
              sx={{ textTransform: 'none' }}
            >
              Unselect all
            </Button>
          </Box>
        }
      >
        {filteredVenues.map((venue) => {
          const isChecked = selectedIdSet.has(venue.id)

          return (
            <MenuItem
              key={venue.id}
              onClick={() => handleToggleVenue(venue.id)}
              dense
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={isChecked}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                />
              </ListItemIcon>
              <ListItemText
                primary={venue.shortName}
                primaryTypographyProps={{
                  noWrap: true,
                  // Native tooltip so an ellipsized name is still readable.
                  title: venue.shortName,
                }}
              />
            </MenuItem>
          )
        })}
      </VenuePanel>
    </Box>
  )
}

export default MultisiteVenueSelector
